import { ClientActionCacheService } from "@cervval.js/client-action/src/services/client.action.cache.service";
import { Display, setDisplay } from "@cervval.js/client-html5-core/src/tools/css.tools";
import { ALIGN_ITEMS, FLEX_CONTAINER, FLEX_ITEM_AUTO, JUSTIFY_CONTENT, PADDING, W_100 } from "@cervval.js/client-html5-core/src/tools/cssClasses.tools";
import { createHtmlElement } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { Tooltip } from "@cervval.js/client-html5-ui/src/bootstrap";
import { ButtonOptions, CjsButton } from "@cervval.js/client-html5-ui/src/components/button/button";
import { CjsCheckbox } from "@cervval.js/client-html5-ui/src/components/checkbox/checkbox";
import { CjsDropdownButton, DropdownButtonOptions } from "@cervval.js/client-html5-ui/src/components/dropdownButton/dropdownButton";
import { CjsIconFa, IconFaOptions } from "@cervval.js/client-html5-ui/src/components/icon/iconFa";
import { CjsSmartTable, SmartTableCell, SmartTableColumn, SmartTableValueEditEvent } from "@cervval.js/client-html5-ui/src/components/smartTable/smartTable";
import { SmartTableCellGetValue } from "@cervval.js/client-html5-ui/src/components/smartTable/smartTableColumn.tools";
import { Action } from "@cervval.js/common-action/src/action.model";
import { ACTION_SERVICE, ActionService } from "@cervval.js/common-action/src/services/action.service";
import { ACTION_CACHE_SERVICE } from "@cervval.js/common-action/src/services/cache.service";
import { SqlDbDefinition } from "@cervval.js/common-sql-core";
import { injector } from "@cervval.js/common/src/dependencies/injector";
import { PropertyConfigType } from "@cervval.js/common/src/forms/propertyConfig.types";
import { getLogConsole } from "@cervval.js/common/src/services/log.service";
import { anyReallyAny, getProp, setProp } from "@cervval.js/common/src/tools/any.tools";
import { compareArrays } from "@cervval.js/common/src/tools/collection.array.tools";
import { RequireOnlyOne, objectKeys } from "@cervval.js/common/src/tools/object.tools";
import { PromiseOrNot, asPromise, isPromise } from "@cervval.js/common/src/tools/promise.tools";
import { FilterOptionsParams } from "@cervval.js/common/src/tools/string.tools";
import { BaseDTO } from "@ehealth_tools/common/src/data/dto/base.dto";
import { ID } from "@ehealth_tools/common/src/data/propertyTypes/property.types";
import { RecursivePartial } from "@ehealth_tools/common/src/types/types.definition";

interface UpdateDTOValueOptions<ElementTypes> {
    action?: Action<ElementTypes, unknown, unknown>;
    /** Callback called after action */
    callback?: () => void;
    /** Callback called during action callback (eg. In encapsulateInAction(action => { .... })) */
    inActionCallback?: (action: Action<ElementTypes, unknown, unknown>, edited: ElementTypes[keyof ElementTypes]) => void;
    /** Updated property name */
    updatedPropertyName?: string;
}

/** Callback when editing a cell 
 * ! Warning: tag column ignored
 */
export function cellEditCallback<ElementTypes, TableName extends keyof ElementTypes & string, T extends ElementTypes[TableName] & BaseDTO<TableName>>(e: CustomEvent<SmartTableValueEditEvent<T>>,
    dbDefinitions: SqlDbDefinition<keyof ElementTypes, unknown>,
    options?: UpdateDTOValueOptions<ElementTypes>): void {

    const dto = e.detail.cell.rowItemData;
    const tableName = dto?.__tableName as TableName | undefined;

    const cacheService = injector.get<ClientActionCacheService<ElementTypes, unknown>>(ACTION_CACHE_SERVICE);

    if (dto != null && tableName != null) {
        const beingEdited = cacheService.getDTOCacheRepository(tableName).findDTOById(dto.id) as T | undefined;
        const propertyName = (options?.updatedPropertyName ?? e.detail.cell.column.propertyConfig?.propertyName) as keyof T & string;
        if (beingEdited != null && propertyName != null) {
            const isAssociation = dbDefinitions.tables.find(v => v.name === tableName)?.associations?.[propertyName as string] != null;
            const originalValue = e.detail.cell.rowItemData?.[propertyName] ?? (isAssociation ? [] as anyReallyAny : undefined);
            updateDTOValue<ElementTypes, TableName, T, keyof T & string>({
                dbDefinitions,
                beingEdited,
                tableName,
                propertyName: propertyName,
                newValue: e.detail.newValue,
                originalValue
            }, options);
        }
    }
}

/** Generic function to setCellValue of conventional DTO */
export function updateDTOValue<ElementTypes, TableName extends keyof ElementTypes & string, T extends ElementTypes[TableName] & BaseDTO<TableName>, K extends keyof T & string>(
    p: {
        dbDefinitions: SqlDbDefinition<keyof ElementTypes, unknown>,
        beingEdited: T,
        tableName: TableName,
        propertyName: K,
        newValue: T[K],
        originalValue?: T[K]
    },
    options?: UpdateDTOValueOptions<ElementTypes>): void {

    const propertyName = p.propertyName as K;
    try {
        if (!options?.action) {

            const actionPersistenceService = injector.get<ActionService<ElementTypes, unknown>>(ACTION_SERVICE);
            actionPersistenceService.encapsulateInAction((action) => {
                _updateOrHandleArray(p.dbDefinitions, action, p.tableName, p.beingEdited, p.propertyName, p.newValue, p.originalValue);
                options?.inActionCallback?.(action, p.beingEdited);
            },
                {
                    logMessage: "Modification d'un objet de type: " + p.tableName + " (" + propertyName.toString() + ")"
                }).then(_ => {
                    p.beingEdited[propertyName] = p.newValue;
                    _updateCachedDTO<ElementTypes, TableName, T, K>(p.beingEdited.__tableName, p.beingEdited.id, propertyName as anyReallyAny, p.newValue);
                    options?.callback?.();
                }).catch(e => getLogConsole().error(e));

        } else {
            _updateOrHandleArray(p.dbDefinitions, options.action, p.tableName, p.beingEdited, p.propertyName, p.newValue, p.originalValue);
            // TODO keep both just in case ???
            options?.inActionCallback?.(options.action, p.beingEdited);
            options?.callback?.();
        }
    } catch (e) {
        getLogConsole().error(e);
    }
}

/** Update the property value of cached DTO*/
function _updateCachedDTO<ElementTypes, Table extends keyof ElementTypes, T extends ElementTypes[Table], K extends keyof T & string>(tableName: Table | undefined, id: ID, propertyName: K, value: T[K]): void {
    if (tableName) {
        const genuineDTO = injector.get<ClientActionCacheService<ElementTypes, unknown>>(ACTION_CACHE_SERVICE).getDTOCacheRepository(tableName).findDTOById(id);
        if (genuineDTO) {
            setProp(genuineDTO, propertyName, value);
        }
    }
}

/** Because array are not handled, auto the operation for array or normal value*/
function _updateOrHandleArray<ElementTypes, Tablename extends keyof ElementTypes, T extends ElementTypes[Tablename], K extends keyof T>(dbDefinitions: SqlDbDefinition<keyof ElementTypes, unknown>, action: Action<ElementTypes, unknown, unknown>, tablename: Tablename, dto: T, propertyName: K, value: T[K], originalValue?: T[K]): void {
    originalValue = originalValue ?? dto[propertyName];

    const isAssociation = dbDefinitions.tables.find(v => v.name === tablename)?.associations?.[propertyName as string] != null;
    if (isAssociation) {
        if (Array.isArray(value)) {
            if (originalValue == null) { originalValue = [] as T[K]; }
            if (Array.isArray(originalValue)) {
                dto[propertyName] = originalValue; // Restore the original value....
                const comp = compareArrays({ newArray: value, oldArray: originalValue, canBeRemovedFct: () => true, matchingElementsFct: (a, b) => a === b, needsUpdateFct: () => false });
                for (const toAdd of comp.toAdd) {
                    action.addToArray(tablename, dto, propertyName as keyof ElementTypes[Tablename], { addReference: { id: toAdd } });
                }
                for (const toRemove of comp.toRemove) {
                    action.removeFromArray(tablename, dto, propertyName as keyof ElementTypes[Tablename], { addReference: { id: toRemove } });
                }
            }
        }
    } else {
        // Clear to force update
        if (value != null) {
            delete dto[propertyName];
        } else {
            dto[propertyName] = 1 as anyReallyAny; // Anything
        }
        action.updateValueIfNeeded(tablename, dto, propertyName, value);
    }
}

/** */
export function showHideRowButtonsOnHover(row: HTMLElement, buttons: (CjsButton | CjsIconFa)[], display = Display.BLOCK): void {
    for (const button of buttons) {
        setDisplay(button, Display.NONE);
    }
    row.addEventListener("mouseenter", () => {
        for (const button of buttons) {
            setDisplay(button, display);
        }
    });
    row.addEventListener("mouseleave", () => {
        for (const button of buttons) {
            setDisplay(button, Display.NONE);
        }
    });
}

/** Add delete button to columns */
export function addActionButtonToColumns<T>(
    p: {
        columns: SmartTableColumn<T>[],
        forceIndice?: number,
        afterColumn?: string,
        btnOptions: Partial<ButtonOptions> | ((cell: SmartTableCell<T>) => (Promise<Partial<ButtonOptions>> | Partial<ButtonOptions>)),
        uidSuffix: string,
        callback: (data: T) => void,
        hide?: (cell: SmartTableCell<T>) => PromiseOrNot<boolean>,
        disabled?: (cell: SmartTableCell<T>) => boolean,
        disableAutoHide?: (cell: SmartTableCell<T>) => boolean,
        width?: string
    }): void {
    const newColumn: SmartTableColumn<T> = {
        uid: "actions" + p.uidSuffix,
        filterDisabled: true,
        sortDisabled: true,
        selectCallbackDisabled: true,
        renderCell: (cell) => {
            const syncContainer = document.createElement("div");
            const editedData = cell.rowItemData;
            const pOptions = typeof p.btnOptions === "function" ? p.btnOptions(cell) : p.btnOptions;

            /** */
            const loadButton: (options: Partial<ButtonOptions>) => Promise<void> = async (options: Partial<ButtonOptions>) => {
                const hidden = await asPromise(p.hide?.(cell));

                if (editedData == null || hidden) { return; }
                if (p.disabled) {
                    options.disabled = p.disabled(cell);
                }
                const btn = new CjsButton(options);
                btn.clickEvent.on(() => {
                    p.callback(editedData);
                });

                const row = cell.row.tr;
                if (row && (!p.disableAutoHide || !p.disableAutoHide(cell))) {
                    showHideRowButtonsOnHover(row, [btn]);
                }
                syncContainer.append(btn);

            };

            if (isPromise(pOptions)) {
                pOptions.then(opt => loadButton(opt)).catch(e => getLogConsole().error(e));
            } else {
                loadButton(pOptions);
            }
            return createHtmlElement("div", {
                cssClass: [FLEX_CONTAINER, W_100, FLEX_ITEM_AUTO, JUSTIFY_CONTENT.END, ALIGN_ITEMS.CENTER, PADDING.PE_2],
                innerHTML: [syncContainer]
            });

        }, width: p.width
    };
    if (p.forceIndice) {
        p.columns.splice(p.forceIndice, 0, newColumn);
    } else if (p.afterColumn) {
        insertCol(newColumn, p.columns, { after: p.afterColumn });
    } else {
        p.columns.push(newColumn);
    }
}


/** Add delete button to columns */
export function addActionDropDownButtonToColumns<T>(
    p: {
        columns: SmartTableColumn<T>[],
        forceIndice?: number,
        afterColumn?: string,
        btnOptions: Partial<DropdownButtonOptions<number>> | ((cell: SmartTableCell<T>) => (Promise<Partial<DropdownButtonOptions<number>>> | Partial<DropdownButtonOptions<number>>)),
        uidSuffix: string,
        itemDefinitions: { icon?: IconFaOptions, label: string, callback: (data: T) => void }[] | ((cell: SmartTableCell<T>) => { icon?: IconFaOptions, label: string, callback: (data: T) => void }[]),
        hide?: (cell: SmartTableCell<T>) => PromiseOrNot<boolean>,
        disabled?: (cell: SmartTableCell<T>) => boolean,
        disableAutoHide?: (cell: SmartTableCell<T>) => boolean,
        width?: string
    }): void {
    const newColumn: SmartTableColumn<T> = {
        uid: "actions" + p.uidSuffix,
        filterDisabled: true,
        selectCallbackDisabled: true,
        renderCell: (cell) => {
            const syncContainer = document.createElement("div");
            const editedData = cell.rowItemData;
            const pOptions = typeof p.btnOptions === "function" ? p.btnOptions(cell) : p.btnOptions;

            /** */
            const loadButton: (options: Partial<DropdownButtonOptions<number>>) => Promise<void> = async (options: Partial<DropdownButtonOptions<number>>) => {
                const hidden = await asPromise(p.hide?.(cell));

                if (editedData == null || hidden) { return; }
                if (p.disabled) {
                    options.disabled = p.disabled(cell);
                }
                const itemDefinitions = typeof p.itemDefinitions === "function" ? p.itemDefinitions(cell) : p.itemDefinitions;
                const btn = new CjsDropdownButton<number>({
                    ...options, openMenuInBody: true, dropDownItemConfigs: itemDefinitions.map((v, i) => {
                        return {
                            label: v.label,
                            icon: v.icon,
                            value: i
                        };
                    })
                });

                // Since tap (Hammer) prevent click event on mobile + smartTable, we have to bound manually the show function to touchEnd
                btn.components.button.addEventListener("touchend", () => {
                    btn.bsDropdown.show();
                });

                btn.itemClickEvent.on((e) => {
                    const index = e.detail.value;
                    if (index != null) {
                        itemDefinitions[index]?.callback(editedData);
                    }
                });

                const row = cell.row.tr;
                if (row && (!p.disableAutoHide || !p.disableAutoHide(cell))) {
                    showHideRowButtonsOnHover(row, [btn]);
                }
                syncContainer.append(btn);

            };

            if (isPromise(pOptions)) {
                pOptions.then(opt => loadButton(opt)).catch(e => getLogConsole().error(e));
            } else {
                loadButton(pOptions);
            }
            return createHtmlElement("div", {
                cssClass: [FLEX_CONTAINER, W_100, FLEX_ITEM_AUTO, JUSTIFY_CONTENT.END, ALIGN_ITEMS.CENTER, PADDING.PE_2],
                innerHTML: [syncContainer]
            });

        }, width: p.width
    };
    if (p.forceIndice) {
        p.columns.splice(p.forceIndice, 0, newColumn);
    } else if (p.afterColumn) {
        insertCol(newColumn, p.columns, { after: p.afterColumn });
    } else {
        p.columns.push(newColumn);
    }
}

/** */
export function addCheckboxToColumns<T>(p: {
    columns: SmartTableColumn<T>[],
    callback: (cell: SmartTableCell<T>, checked: boolean) => void,
    isChecked?: (data: T) => boolean,
    isDisabled?: (data: T) => boolean,
    doNotPropagateAtCreate?: boolean
}): void {
    p.columns.push({
        uid: "checkbox",
        filterDisabled: true,
        // Mettre un filtre checkbox
        renderCell: (cell) => {
            const disabled = p.isDisabled && cell.rowItemData != null && p.isDisabled(cell.rowItemData);
            const checkbox = new CjsCheckbox({ disabled: () => disabled });

            checkbox.checkEvent.on(() => {
                p.callback(cell, true);
            });
            checkbox.uncheckEvent.on(() => {
                p.callback(cell, false);
            });
            // Check it if needed
            if (p.isChecked && cell.rowItemData && p.isChecked(cell.rowItemData)) {
                checkbox.setValue(true, !disabled || p.doNotPropagateAtCreate);
            }

            return createHtmlElement("div", {
                cssClass: [FLEX_CONTAINER, W_100, FLEX_ITEM_AUTO, JUSTIFY_CONTENT.CENTER, ALIGN_ITEMS.CENTER],
                innerHTML: [checkbox]
            });

        },
        width: "5%"
    });
}

/** */
export function addOkMarkerToColumns<T>(
    columns: SmartTableColumn<T>[],
    label: string,
    isValid: (cell: SmartTableCellGetValue<T>) => { status: boolean | undefined, tooltip?: string },
    options?: { hide?: (cell: SmartTableCell<T>) => boolean, uidPosition?: RequireOnlyOne<{ after: string, before: string }>, showValid?: boolean, showInvalid?: boolean, showUndefined?: boolean, showFilter?: boolean, width?: string, uidSuffix?: string }
): void {
    const validMark = options && options.showValid === false ? "" : "✔️";
    const invalidMark = options && options.showInvalid === false ? "" : "❌";
    const undefinedMark = options && options.showUndefined === false ? "" : "⌛";
    const col: SmartTableColumn<T> = {
        uid: "markers" + (options?.uidSuffix ?? ""),
        filterDisabled: options?.showFilter ? false : true,
        selectCallbackDisabled: true,
        propertyConfig: {
            label,
            propertyName: "xoxo",
            typeUid: PropertyConfigType.STRING
        },
        getCellValue: (cell) => isValid(cell).status,
        renderCell: (cell) => {
            const hidden = options?.hide ? options?.hide(cell) : false;
            if (hidden) {
                return "";
            }

            const valid = isValid(cell);
            const ico = createHtmlElement("div", {
                cssClass: [W_100, FLEX_ITEM_AUTO],
                innerHTML: [valid.status ? validMark : valid.status === false ? invalidMark : undefinedMark]
            });

            if (valid.tooltip) {
                new Tooltip(ico, { container: document.body, title: valid.tooltip });
            }
            return ico;

        },
        filterComparator: (p) => {
            if (p.itemValue == null) { return false; }
            return (p.filterValue === "ok" && p.itemValue === true) || (p.filterValue === "ko" && p.itemValue === false);
        },
        width: options?.width ?? "10px"
    };
    if (options?.uidPosition) {
        insertCol(col, columns, options.uidPosition);
    } else {
        columns.push(col);
    }
}

/** */
export function setColOption<T>(columns: SmartTableColumn<T>[], uid: string, options: RecursivePartial<SmartTableColumn<T>>): void {
    const col = columns.find(v => v.uid === uid);
    if (col == null) { return; }
    for (const colKey of objectKeys(options)) {
        const original = col[colKey];
        const newOptions = options[colKey];
        if (typeof original === "object" && typeof newOptions === "object") {
            setProp(col, colKey, { ...original, ...newOptions });
        }
        setProp(col, colKey, options[colKey]);
    }
}

/** set options of all columns in a table */
export function setATableColumnsOption<T>(smartTable: CjsSmartTable<T>, options: RecursivePartial<SmartTableColumn<T>>): void {
    for (const column of smartTable.options.columns) {
        setColOption(smartTable.options.columns, column.uid, options);
    }
}

/** */
export function insertCol<T>(column: SmartTableColumn<T>, columns: SmartTableColumn<T>[], uid: RequireOnlyOne<{ after: string, before: string }>): void {
    const index = columns.map(v => v.uid).indexOf(uid.after ?? uid.before);
    if (index === -1) {
        console.error("Erreur d'inex de colonnes dans insertCol", uid.after ?? uid.before);
    }
    if (uid.after != null && index >= 0) {
        columns.splice(index + 1, 0, column);
    }
    if (uid.before != null && index >= 0) {
        columns.splice(index, 0, column);
    }
}

/** */
export function regexFilterFromStart<T>(params: FilterOptionsParams<T>): boolean {
    if (!params.filterValue?.length) { return true; }
    const rgxp = new RegExp("^" + params.filterValue);
    return params.itemValue?.toString().match(rgxp) != null;
}

/**
 * Function to use for property `setCellValue` of a smartTableColumn.
 * With it, you can check the value you are about to persist, and in case an error occurred,
 * the value will not be persisted.
 * ! The function will not work for columns w<ith a propertyName different than an attribute of the object being edited on the smartTable
 * @param params
 * @param params.cell The cell being edited
 * @param params.newValue The new value for the cell
 * @param params.checkValueToPersist Callback to check the new value of the cell, and return a typed value. In case of error, this callback should throw in order to rollback to previous value
 * @param params.persistValue  Callback to persist new value of column
 */
export async function setCellValueWithRollback<TableType, ValueType>(params: {
    cell: SmartTableCell<TableType>,
    newValue: anyReallyAny,
    checkValueToPersist: (object: TableType, column: string, value: anyReallyAny) => ValueType,
    persistValue: (object: TableType, value: ValueType) => Promise<void>
}): Promise<void> {
    const { cell, newValue, checkValueToPersist, persistValue } = params;
    const oldRowValue = cell.rowItemData;
    const editedColumnPropertyConfig = cell.column.propertyConfig;

    if (oldRowValue == null || editedColumnPropertyConfig == null) {
        return;
    }
    const editedAttribute = editedColumnPropertyConfig.propertyName;
    if (!Object.keys(oldRowValue).includes(editedAttribute)) {
        return;
    }

    const oldCellValue = getProp(oldRowValue, editedAttribute);

    try {
        const value = checkValueToPersist(oldRowValue, editedAttribute, newValue);
        await persistValue(oldRowValue, value);
        // In any case we ensure the object value is set on edited object
        setProp(oldRowValue, editedAttribute, value);
    } catch (err) {
        getLogConsole().error(err);
        // In case of error, we rollback to the previous value registered
        setProp(oldRowValue, editedAttribute, oldCellValue);
    }
}